import { useState, useCallback, useEffect } from "react";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { useLocalization } from "../../context/LocalizationContext";
import { useAppContext } from "../../context/AppContext";
import { useAIAvatarSession } from "../../context/AIAvatarSessionContext";
import { fetchPodSchedule } from "../../utils/api";
import { usePodData } from "../../hooks/usePodData";
import { isPodAvailable } from "../../utils/helpers";
import { useEndSession } from "../../hooks/useEndSession";
import { useCustomAvatarVoice } from "../../hooks/useCustomAvatarVoice";
import { DASHBOARD_BUTTON_CONFIG } from "../../constants";
import Header from "../../containers/Header";
import Modal from "../../components/shared/Modal";
import EndSessionConfirmModal from "../../components/shared/EndSessionConfirmModal";
import CircularButton from "../../components/shared/CircularButton";
import micIcon from "../../assets/icons/AI/whiteMic.svg";
import "./Dashboard.css";
import Button from "../../components/shared/Button";
import { formatUserName } from "../../utils/helpers";
import { UserData } from "../../dtos/patient";
import Loader from "../../components/shared/Loader";

interface DashboardButtonConfig {
  id: string;
  icon: string;
  labelKey: string;
  action: string;
  route?: string;
  messageKey?: string;
}

interface DashboardProps {
  userData: UserData | null;
}

const Dashboard = ({ userData }: DashboardProps) => {
  const navigate = useAppNavigate();
  const { t, language, isRTL } = useLocalization();
  const { completedSteps, aiLanguage } = useAppContext();
  const { podId } = usePodData();
  const { showConfirm, isEnding, requestEndSession, confirmEndSession, cancelEndSession } = useEndSession();
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  const {
    videoRef,
    sessionToken,
    sessionId,
    isVideoReady,
    isConnected,
    isVoiceChatActive,
    transcription,
    avatarSpeaking,
    repeat,
    initializeSession,
    isAgentConnecting,
    setIsAgentConnecting,
    agentError,
    setAgentError,
    setUserName,
  } = useAIAvatarSession();

  const {
    isRecording,
    startRecording,
    stopRecording,
  } = useCustomAvatarVoice({
    sessionId,
    repeat,
    language: aiLanguage,
  });

  const displayUserData = userData;
  const userName = formatUserName(displayUserData?.name ?? "");

  const handleButtonClick = async (config: DashboardButtonConfig) => {
    if (config.action === "navigate" && config.route) {
      if (config.id === "consultation") {
        await handleConsultationClick();
      } else {
        navigate(config.route);
      }
    } else if (config.action === "endSession") {
      requestEndSession();
    } else if (config.action === "placeholder" && config.messageKey) {
      console.log(t(config.messageKey) + " clicked");
    }
  };

  const handleConsultationClick = async () => {
    try {
      setIsCheckingAvailability(true);
      const scheduleData = await fetchPodSchedule(Number(podId), 4, 1, 1);
      const available = isPodAvailable(scheduleData);
      if (available) {
        navigate("/consultation");
      } else {
        setShowUnavailableModal(true);
      }
    } catch (error) {
      console.error("Error checking pod availability:", error);
      setShowUnavailableModal(true);
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const handleStartSpeaking = useCallback(async () => {
    // First time: initialize session
    if (!sessionToken && !isAgentConnecting) {
      try {
        await initializeSession();
      } catch {
        setAgentError(t("liveAvatar.failedToConnect"));
      }
      return;
    }

    // Toggle recording once session is connected
    if (isConnected) {
      if (isRecording) {
        stopRecording();
      } else {
        await startRecording();
      }
    }
  }, [
    sessionToken,
    isConnected,
    isRecording,
    startRecording,
    stopRecording,
    initializeSession,
    isAgentConnecting,
    setAgentError,
    t,
  ]);

  // Automatically initialize shared AI agent session on dashboard load
  useEffect(() => {
    if (!sessionToken && !isAgentConnecting) {
      initializeSession().catch((err) => {
        console.error("Avatar init error:", err);
        setAgentError(t("liveAvatar.failedToConnect"));
      });
    }
  }, [sessionToken, isAgentConnecting, initializeSession, setAgentError, t]);

  useEffect(() => {
    setUserName(displayUserData?.name);
  }, [displayUserData?.name, setUserName]);

  // return (isAgentConnecting && !isEnding) ? (
  //   <div className="loading-screen">
  //     <Loader text={t("dashboard.sessionConnecting")} subText={t("dashboard.sessionConnectingSubText")} type="avatar-oriented" />
  //   </div>
  // ) : 
  return (
    <div className="dashboard-page page-container  scrollable-page ">
      <Header
        statusText={t("settings.activeSession")}
        title={t("dashboard.welcomeMessage", { name: userName })}
        showSettingsButton={true}
      />

      <div className="dashboard-content">
        <div className="dashboard-avatar-section">
          <div className="dashboard-avatar-frame">
            {avatarSpeaking && <div className="dashboard-avatar-ring" />}
            <div id="dashboard-avatar-inner" className="dashboard-avatar-inner">
              {!isVideoReady && !isAgentConnecting && !agentError && !transcription && (
                <div className="dashboard-avatar-placeholder" />
              )}
              {(isAgentConnecting || !isVideoReady) && (
                <div className="dashboard-avatar-loading">
                  <span>{t("dashboard.connectingToAgent")}</span>
                </div>
              )}
              {agentError && (
                <div className="dashboard-avatar-error">
                  <span>{agentError}</span>
                </div>
              )}
              <video
                ref={videoRef}
                className="dashboard-avatar-video"
                playsInline
                autoPlay
                muted={false}
                style={{ display: isVideoReady ? "block" : "none" }}
                aria-label={t("settings.aiAssistant")}
              />
            </div>
          </div>
        </div>

        <p className="dashboard-instruction page-text">
          {transcription}
        </p>
        <div className="dashboard-actions-container">
          <div className="action-buttons">
            {DASHBOARD_BUTTON_CONFIG.map((config: DashboardButtonConfig) => {
              const label = t(config.labelKey);
              return (
                <CircularButton
                  isCompleted={!!completedSteps[config.id]}
                  key={config.id}
                  icon={config.icon}
                  label={label}
                  onClick={() => handleButtonClick(config)}
                  isLoading={config.id === "consultation" && isCheckingAvailability}
                  loadingLabel={t("consultation.checkingAvailability")}
                  disabled={config.id === "end" && isEnding}
                  variant={config.id === "end" ? "danger" : "default"}
                />
              );
            })}
          </div>
          <Button
            variant="secondary"
            icon={micIcon}
            iconPosition="start"
            className="bubbling-ai-modal-start-speaking"
            onClick={handleStartSpeaking}
            disabled={isAgentConnecting}
            iconStyle={
              isRTL
                ? { transform: "scaleX(-1)", marginLeft: "1vw", marginRight: 0 }
                : { marginRight: "1vw" }
            }
          >
            {isAgentConnecting
              ? t("dashboard.connectingToAgent")
              : isRecording ? t("dashboard.listening") : t("dashboard.startSpeaking")}
          </Button>
        </div>

      </div>

      <Modal
        isOpen={showUnavailableModal}
        onClose={() => setShowUnavailableModal(false)}
        title={t("consultation.notAvailable")}
        message={t("consultation.notAvailableMessage")}
      />
      <EndSessionConfirmModal
        isOpen={showConfirm}
        onClose={cancelEndSession}
        onConfirm={confirmEndSession}
        isEnding={isEnding}
      />
    </div>
  );
};

export default Dashboard;
